/**
 * "Which class will your child start with?" — the start date for a fixed-term series billed by
 * the class (`programs.prorate_late_joins`, BHCS-152).
 *
 * The parent's pick is the billing start date: the invoice charges the classes from it to the end
 * of the term. So the picker offers only real classes that have not happened yet, and once a class
 * is picked it lists every date in the term — the ones being paid for, the ones before the start,
 * and the days with no class — so the count on the invoice can be checked against a calendar.
 */

import { Check } from 'lucide-react';
import { formatWeekdayDateDisplay } from '../utils/date';
import { parentClosureLabel } from '../utils/sessionBreakdown';
import { termDays, type ClassBilledProgram, type TermDay } from '../utils/lateJoinProration';

interface FirstClassPickerProps {
  program: ClassBilledProgram;
  /** The classes still on offer (`firstClassOptions`), oldest first. */
  options: readonly string[];
  /** The chosen first class, or '' when none is chosen yet. */
  value: string;
  onChange: (ymd: string) => void;
  /** Today in the business timezone, to label today's class. */
  today: string;
}

function dayChipClass(day: TermDay): string {
  switch (day.status) {
    case 'billed':
      return 'bg-blue-50 text-blue-800 border-blue-200';
    case 'before':
      return 'bg-gray-50 text-gray-400 border-gray-200 line-through';
    default:
      return 'bg-amber-50 text-amber-800 border-amber-200';
  }
}

function dayChipNote(day: TermDay): string | undefined {
  if (day.status === 'before') return 'before your start';
  if (day.status === 'no-class') return parentClosureLabel(day.reason) ?? 'No class';
  return undefined;
}

export function FirstClassPicker({ program, options, value, onChange, today }: FirstClassPickerProps) {
  const days = value ? termDays(program, value) : [];
  const billed = days.filter((d) => d.status === 'billed').length;
  const total = days.filter((d) => d.status !== 'no-class').length;

  return (
    <div className="mb-4" data-testid="first-class-picker">
      <p id="first-class-label" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
        Which class will your child start with?
      </p>
      <p className="text-xs text-gray-500 mb-2">
        You pay only for the classes from the one you pick to the end of the term.
      </p>
      <div role="radiogroup" aria-labelledby="first-class-label" className="flex flex-wrap gap-2">
        {options.map((ymd) => {
          const selected = ymd === value;
          return (
            <button
              key={ymd}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(ymd)}
              className={`px-3 py-1.5 rounded-full border text-xs sm:text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                selected
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'
              }`}
            >
              {formatWeekdayDateDisplay(ymd)}
              {ymd === today && <span className={selected ? 'text-blue-100' : 'text-blue-600'}> · today</span>}
            </button>
          );
        })}
      </div>

      {value ? (
        <div className="mt-3 rounded-lg border border-gray-200 p-3" data-testid="billed-classes">
          <p className="text-xs sm:text-sm font-medium text-gray-900 mb-2">
            Classes you&apos;ll pay for: {billed} of {total}
          </p>
          <ul className="flex flex-wrap gap-1.5">
            {days.map((day) => {
              const note = dayChipNote(day);
              return (
                <li
                  key={day.date}
                  data-status={day.status}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] sm:text-xs ${dayChipClass(day)}`}
                  title={note}
                >
                  {day.status === 'billed' && <Check className="w-3 h-3" aria-hidden="true" />}
                  <span>{formatWeekdayDateDisplay(day.date)}</span>
                  {day.status === 'no-class' && <span className="no-underline">· {note}</span>}
                  {note && day.status !== 'no-class' && <span className="sr-only">({note})</span>}
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <p className="mt-2 text-xs text-amber-700">Pick a class to see the dates and the price.</p>
      )}
    </div>
  );
}
